import { isNil } from "lodash";
import { Typography } from "@mui/material";
import { useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import { Loader } from "@googlemaps/js-api-loader";
import { Control } from "react-hook-form";

import { FormInputText } from "./FormInputText";
import { others } from "../../styles/colors";
import fontWeight from "../../styles/mui/fontWeight";
import { googleMapKey } from "../../constants/Config";
import { AddressField } from "../styles/styles";
import { getValue } from "../../utils";

interface AddressProps {
  control: Control<any, any>;
  getValues: any;
  labels: any;
  nestedObj?: string;
  reset: any;
  searchInput: React.RefObject<null>;
  setValue?: any;
  addressLabel?: string;
  addressVariant?: any;
  addressStyle?: string;
  disable?: boolean;
  clearErrors?: any;
  agencyDetails?: boolean;
}

const initMapScript = () => {
  const loader = new Loader({
    apiKey: googleMapKey,
    version: "weekly",
  });
  return loader.importLibrary("places");
};

const extractPatientAddress = (place: any) => {
  const address: any = {
    city: null,
    patientState: null,
    zipCode: null,
    streetName1: null,
    streetName2: null,
    county: null,
    country: null,
    streetNumber: null,
    latitude: null,
    longitude: null,
  };

  let township: string | null = null;

  if (!Array.isArray(place?.address_components)) {
    return address;
  }

  place.address_components.forEach(
    (component: {
      types: Array<string>;
      long_name: string;
      short_name: string;
    }) => {
      const types = component.types;
      const value = component.long_name;
      const short_name = component.short_name;

      if (types.includes("locality")) {
        address.city = value;
      }

      if (types.includes("administrative_area_level_3")) {
        township = value;
      }

      if (types.includes("street_number")) {
        address.streetNumber = value;
      }

      if (types.includes("route")) {
        address.streetName1 = value;
      }

      if (types.includes("neighborhood")) {
        address.streetName2 = value;
      }

      if (types.includes("administrative_area_level_2")) {
        address.county = value;
      }

      if (types.includes("administrative_area_level_1")) {
        address.patientState = value;
      }

      if (types.includes("postal_code")) {
        address.zipCode = value;
      }

      if (types.includes("country")) {
        address.country = short_name;
      }
    }
  );

  if (getValue(place, "geometry.location")) {
    address.latitude = place.geometry.location?.lat() || null;
    address.longitude = place.geometry.location?.lng() || null;
  }

  if (!address.city && township) {
    address.city = township;
  }

  return address;
};

type AllProps = AddressProps;

export const AddressFormInputUseForm: React.FC<AllProps> = ({
  control,
  getValues,
  labels,
  nestedObj,
  reset,
  searchInput,
  setValue,
  addressLabel,
  addressVariant,
  addressStyle,
  disable,
  agencyDetails,
}: AllProps) => {
  const initAutocomplete = () => {
    if (searchInput.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        searchInput.current
      );
      autocomplete.setFields(["address_component", "geometry"]);
      autocomplete.addListener("place_changed", () =>
        onChangeAddress(autocomplete)
      );
    }
  };

  const getStreetName1 = (
    streetNumber: string | null,
    streetName1: string | null
  ) =>
    streetNumber && streetName1
      ? streetNumber + " " + streetName1
      : streetName1;

  const onChangeAddress = (autocomplete: any) => {
    const place = autocomplete.getPlace();
    const address = extractPatientAddress(place);
    const {
      streetName1,
      streetName2,
      county,
      city,
      patientState,
      zipCode,
      streetNumber,
      latitude,
      longitude,
    } = address;

    let obj = {
      [labels[0].name]: getStreetName1(streetNumber, streetName1),
      [labels[1].name]: streetName2,
      [labels[2].name]: city,
      [labels[3].name]: patientState,
      [labels[4].name]: county,
      [labels[5].name]: zipCode,
    };
    if (labels.length > 6) {
      obj = {
        ...obj,
        [labels[6].name]: latitude,
        [labels[7].name]: longitude,
      };
    }
    if (streetName1 !== null && county !== null) {
      if (isNil(setValue)) {
        if (nestedObj) {
          // if form has nested objects
          const object = getValues(nestedObj);
          reset({
            ...getValues(),
            [nestedObj]: {
              ...object,
              ...obj,
            },
          });
        } else {
          reset({
            ...getValues(),
            ...obj,
          });
        }
      } else {
        setValue(labels[0].name, getStreetName1(streetNumber, streetName1), {
          shouldValidate: true,
        });
        setValue(labels[1].name, streetName2, {
          shouldValidate: true,
        });
        setValue(labels[2].name, city, {
          shouldValidate: true,
        });
        setValue(labels[3].name, patientState, {
          shouldValidate: true,
        });
        setValue(labels[4].name, county, {
          shouldValidate: true,
        });
        setValue(labels[5].name, zipCode, {
          shouldValidate: true,
        });
        if (labels.length > 6) {
          setValue(labels[6].name, latitude, {
            shouldValidate: true,
          });
          setValue(labels[7].name, longitude, {
            shouldValidate: true,
          });
        }
      }
    }
  };

  useEffect(() => {
    initMapScript().then(() => initAutocomplete());
  }, []);

  // creates dynamic names according to nested object
  const namesForNestedObj = (keyName: string, name: string) => {
    return `${keyName}.${name}`;
  };

  const renderAddressInputFields = () => {
    // skip lat and long labels (hidden fields)
    return labels.slice(0, 6).map((label: any, index: number) => (
      <Grid size={{ xs: label.xs || 1.5 }} sx={AddressField} key={label.name}>
        <FormInputText
          name={
            nestedObj ? namesForNestedObj(nestedObj, label.name) : label.name
          }
          placeholder="Add here..."
          control={control}
          label={label.label}
          textLength={label.textLength}
          searchInputRef={index === 0 ? searchInput : null}
          helper={label.helper || undefined}
          disabled={disable}
        />
      </Grid>
    ));
  };

  if (agencyDetails) {
    return renderAddressInputFields();
  }

  return (
    <>
      <Grid container>
        {addressLabel && addressVariant && (
          <Grid size={{ xs: 12 }} sx={{ padding: "0.5rem 0 " }}>
            <Box sx={{ textAlign: "left" }}>
              <Typography
                variant={addressVariant}
                color={others.otherColors[63]}
                fontWeight={fontWeight[5]}
                mb={addressStyle}
              >
                {addressLabel}
              </Typography>
            </Box>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Grid container>{renderAddressInputFields()}</Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default AddressFormInputUseForm;
